import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Inject, NgModule, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CheckBoxInputModule } from '@commons/checkbox-input/checkbox-input.component';
import { CheckmarkLoaderModule } from '@commons/contact-modal/checkmark-loader/checkmark-loader.component';
import { FormRgpdInfoModule } from '@commons/form-rgpd-info/form-rgpd-info.component';
import { WizValidators } from '@commons/validators';
import {
  DEFAULT_ZIPCODE_CONFIG,
  ZipcodeInputPoleEmploiModule,
} from '@commons/zipcode-input-pole-emploi/zipcode-input-pole-emploi.component';
import { WindowResizeService } from '@core/services/window-resize/window-resize.service';
import { environment } from '@environment';
import { toCejCity } from '@models/cej/cej-city';
import { CejV2RecontactInfo } from '@models/cej/cej-recontact-info';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CejService } from '@webservices/event-api/cej.webservice';
import { WINDOW } from '@wizbii/angular-utilities';
import { trackConversion, trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, Observable } from 'rxjs';

@UntilDestroy()
@Component({
  templateUrl: './cej-v2-contact-modal.component.html',
  styleUrls: ['./cej-v2-contact-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CejV2ContactModalComponent implements OnInit, OnDestroy {
  @HostBinding('class.dialog-form') dialogForm = true;

  zipCodeFieldConfig = { ...DEFAULT_ZIPCODE_CONFIG, required: true };
  get ratio$(): Observable<number> {
    return this.windowResizeService.ratio$;
  }

  get firstName(): AbstractControl {
    return this.cejContactForm.get('firstName') as AbstractControl;
  }

  get lastName(): AbstractControl {
    return this.cejContactForm.get('lastName') as AbstractControl;
  }

  get email(): AbstractControl {
    return this.cejContactForm.get('email') as AbstractControl;
  }

  get phone(): AbstractControl {
    return this.cejContactForm.get('phoneNumber') as AbstractControl;
  }

  get age(): AbstractControl {
    return this.cejContactForm.get('age') as AbstractControl;
  }

  get city(): AbstractControl {
    return this.cejContactForm.get('city') as AbstractControl;
  }

  get cejContactFormValue(): CejV2RecontactInfo {
    return this.cejContactForm.value as CejV2RecontactInfo;
  }

  get rgpdInfo(): string {
    return `En cliquant sur "${this.submitButtonLabel}", j’accepte d’être recontacté par Pôle Emploi ou la Mission Locale la plus proche de chez moi, dans le cadre du Contrat d’Engagement Jeune`;
  }

  isMobile = false;
  cejContactForm!: FormGroup;
  saved$ = new BehaviorSubject<boolean>(false);
  saving$ = new BehaviorSubject<boolean>(false);
  submitButtonLabel = 'Envoyer la demande';
  ages = [...Array(15).keys()].map((item) => item + 16);

  trackEvent = trackEventWithAtInternet;

  constructor(
    readonly dialogRef: MatDialogRef<CejV2ContactModalComponent>,
    @Inject(WINDOW) private readonly window: any,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly windowResizeService: WindowResizeService,
    private readonly cejService: CejService,
    private readonly formBuilder: FormBuilder
  ) {
    this.isMobile = data.isMobile;
    this.cejContactForm = this.buildForm();
  }

  ngOnInit(): void {
    if (this.isMobile) {
      this.dialogRef
        .backdropClick()
        .pipe(untilDestroyed(this))
        .subscribe({ next: () => this.closeDialog() });

      this.ratio$.pipe(untilDestroyed(this)).subscribe({
        next: (ratio) => {
          this.dialogRef.updateSize('100vw', `${100 * ratio - 50}px`);
          this.dialogRef.addPanelClass('wizbii-mobile-panel-open');
        },
      });
    }
  }

  doSubmit(): void {
    this.saving$.next(true);

    const contactInfos: CejV2RecontactInfo = {
      ...this.cejContactFormValue,
      city: toCejCity(this.city.value),
    };

    this.cejService
      .registerForFutureContact(contactInfos)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.trackEvent({
            category: 'CEJ',
            action: 'Click Envoyé',
            type: 'action',
          });

          if (environment.platform === 'prod') {
            trackConversion({
              allow_custom_scripts: true,
              send_to: 'DC-3048978/forma0/btnenvoi+standard',
            });
          }

          this.saving$.next(false);
          this.saved$.next(true);
        },
      });
  }

  closeDialog(): void {
    if (this.isMobile) {
      this.dialogRef.addPanelClass('wizbii-mobile-panel-close');
      this.window.setTimeout(() => {
        this.dialogRef.close();
      }, 100);
    } else {
      this.dialogRef.close();
    }
  }

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      firstName: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      lastName: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      email: [undefined, [Validators.required, WizValidators.email]],
      city: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      age: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      phoneNumber: [undefined, [Validators.required, WizValidators.phone]],
      acceptToReceiveInfos: [false],
    });
  }

  trackByAge = (_: number, age: number): number => age;

  ngOnDestroy(): void {
    this.saved$.complete();
    this.saving$.complete();
  }
}

@NgModule({
  imports: [
    CheckmarkLoaderModule,
    CommonModule,
    FormRgpdInfoModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    ZipcodeInputPoleEmploiModule,
    CheckBoxInputModule,
  ],
  declarations: [CejV2ContactModalComponent],
  exports: [CejV2ContactModalComponent],
})
export class CejV2ContactModalModule {}
